/**
 * A1, the daily loose-ends check (wave C, decision 0071, spec §5). Once a day at `Cadence.dailyCheckHour` (absent = 9,
 * null = off) in the project's zone, the Project DO's clock pass calls {@link looseEndsDelta}. Project state only: no
 * model, no runner, no GitHub call. Pure.
 *
 * | Loose end                    | Threshold                                   | Key                         |
 * | A system card waiting on a person | 24 h                                    | `card:<proposalId>`         |
 * | The lead's own card waiting on a person (0158) | about 3 days                  | `aged:<proposalId>`         |
 * | An open PR                   | open 2 days, or checks failing, or conflicts | `pr:<artifactId>:<state>`   |
 * | A KPI without a sample       | twice its cadence                           | `kpi:<kpiId>`               |
 * | An ask to an agent unanswered | 24 h                                       | `ask:<commentId>`           |
 * | A message nobody picked up (0142) | a person's dot or session, 24 h   | `undelivered:<commentId>:<participantId>` |
 * | An item stuck on a blocker   | 24 h                                        | `stuck:<itemId>`            |
 * | An item in review, no review running (0154) | 1 h at an agent or validator stage | `review:<itemId>:r<round>:<stage>` |
 * | Nothing planned (0084)       | nothing that can start or is under way (0158: work under a release waiting for its Start does not count) | `idle:<n>` (n = 3-day steps) |
 * | A run waiting for a runner   | 12 h                                        | `runner:<runId>`            |
 * | An occurrence past next due  | open when the series' next time came        | `occurrence:<itemId>`       |
 * | An active goal without a KPI | no KPIs and no target                       | `goal-kpi:<goalId>`         |
 * | Pages missing (bootstrap)    | status.md or the lead's assumptions.md; an area lead's areas/<slug>/status.md (0086) | `pages:<agentId>` |
 * | The lead's briefs due (0098, 0130) | its last brief ≥ 20 h old and records changed since, or (0158) a card of its own aged since | `brief:<yyyy-mm-dd>`       |
 * | A memory due for a re-check (0139) | a learning past its `revisitAt` | `revisit:<memoryId>:r<revision>` |
 * | Memory tending (0146)        | every 10 memory changes, ≥ 5 current claims | `tend:<step>`               |
 *
 * Overdue items keep their own wake. Decision 0084 (§5): a card an agent or worker proposed is not a loose end (it
 * woke the agent about its own pending card); system cards about an item still go to its agent. The cards that hold an
 * agent's own wakes (the chain card, the timed cap) are not loose ends of that agent either. Decision 0158 (0133 §5
 * amended, Jannik): the one exception is a card the lead itself opened that has waited on a person for about three
 * days (sobremesa's P-2 sat 9 days unseen). It becomes the lead's loose end once and makes its briefs due once; the
 * lead decides it, withdraws it, or raises it in that person's brief. The person is never pushed again.
 */

import { areaStatusPath } from "@minga/protocol"
import { memoryTendingDue } from "./knowledge/memory-review"
import { kpiFeeders } from "./kpi-history"
import {
  idleReviewLine,
  idleReviews,
  longWaitLine,
  longWaits,
  mergedOverReview,
  mergedOverShort,
} from "./lead-insights"
import { projectZone } from "./monitors"
import { isWorkingMember, recipientOf } from "./multiplayer"
import { isCardFor, isStaleEscalation } from "./needs-you"
import { areaStatuses, makesReady, pageOf } from "./pages"
import { agentAbove } from "./people"
import { readiness } from "./readiness"
import { nextCronAt, periodOf } from "./recurrence"
import { goIntentKey } from "./runs"
import { isOpenItem, isProjectAdmin, itemOf, leadOf } from "./state"
import { failedRunsFor } from "./turn-ends"
import type { AgentId, Ids, Item, MemoryItem, ProjectState, Proposal } from "./types"
import { areaLeadOf } from "./workspace-pages"

type UserId = Ids["UserId"]

/** One loose end: its key, the agent who owns its subject (item owner, the card's proposer, else the lead), one line. */
export interface LooseEnd {
  readonly key: string
  readonly agentId: AgentId | null
  readonly line: string
}

const HOUR = 3_600_000
const DAY = 24 * HOUR
/** A card, an ask or a blocked item is a loose end after this long. */
export const LOOSE_END_AFTER_MS = DAY
/** An open PR is a loose end after this long (sooner when its checks fail or it has conflicts). */
export const PR_LOOSE_AFTER_MS = 2 * DAY
/** At most this many keys in one check (NoteLooseEnds' cap). */
export const MAX_LOOSE_ENDS = 500
const CADENCE_MS = { hourly: HOUR, daily: DAY, weekly: 7 * DAY } as const

const age = (ms: number): string => {
  const h = Math.floor(ms / HOUR)
  if (h < 48) return `${h} h`
  return `${Math.floor(ms / DAY)} days`
}

const firstLine = (text: string, max = 100): string => {
  const line = (text.split("\n").find((l) => l.trim() !== "") ?? "").trim()
  return line.length > max ? `${line.slice(0, max - 1)}…` : line
}

const nameOf = (s: ProjectState, userId: string): string =>
  s.members.find((m) => m.userId === userId)?.displayName ?? "a person"

/** The agent an item's loose end goes to: its owner agent, else the agent above it. */
const itemAgent = (s: ProjectState, t: Item | undefined): AgentId | null =>
  !t ? null : t.owner?._tag === "agent" ? t.owner.agentId : (agentAbove(s, t)?.id ?? null)

/** Whose loose end a card is: a system card about an item: that item's agent (agents' own cards are none, 0084). */
const cardAgent = (s: ProjectState, p: Proposal): AgentId | null => {
  const subject = p.body._tag === "question" ? p.body.subject : null
  if (subject?._tag === "item") return itemAgent(s, itemOf(s, subject.id))
  const blocked = p.blocks.find((b) => b._tag === "item")
  return blocked?._tag === "item" ? itemAgent(s, itemOf(s, blocked.id)) : null
}

/** A card that holds an agent's own wakes (Wave A's chain card, wave C's timed cap): not that agent's loose end. */
const holdsWakes = (p: Proposal): boolean =>
  p.body._tag === "question" && p.body.escalation !== null && p.body.subject?._tag === "agent"

/**
 * Decision 0158 (0133 §5 amended): a card the lead opened becomes its loose end after it has waited on a person this
 * long. When the line appears, not a limit: the card stays open and the person is not pushed again.
 */
export const AGED_CARD_MS = 3 * DAY

/** Decision 0158: one of the lead's own cards that has waited on a person for days. */
export interface AgedCard {
  readonly proposal: Proposal
  readonly userId: UserId
  /** When it became aged (created + {@link AGED_CARD_MS}). */
  readonly agedAt: number
}

/** The lead's own open cards waiting on a person for {@link AGED_CARD_MS} or more, oldest first. */
export const agedLeadCards = (s: ProjectState, now: number): ReadonlyArray<AgedCard> => {
  const lead = leadOf(s)
  if (!lead) return []
  const out: AgedCard[] = []
  for (const p of s.proposals) {
    if (p.state !== "open" || p.approver._tag !== "human") continue
    if (p.proposedBy._tag !== "agent" || p.proposedBy.agentId !== lead.id) continue
    if (now - p.createdAt < AGED_CARD_MS || holdsWakes(p) || isStaleEscalation(s, p)) continue
    out.push({ proposal: p, userId: recipientOf(s, p), agedAt: p.createdAt + AGED_CARD_MS })
  }
  return out.sort((a, b) => a.agedAt - b.agedAt)
}

/** "P-2 (Jannik, 9 days)" */
const agedShort = (s: ProjectState, a: AgedCard, now: number): string =>
  `${a.proposal.ref} (${nameOf(s, a.userId)}, ${age(now - a.proposal.createdAt)})`

/** Decision 0158: the loose-end line for one aged card of the lead's: what is true and what usually helps. */
export const agedCardLine = (s: ProjectState, a: AgedCard, now: number): string => {
  const p = a.proposal
  const who = nameOf(s, a.userId)
  const head = `Your card ${p.ref} "${firstLine(p.oneLine)}" has waited on ${who} for ${age(now - p.createdAt)}`
  // a person's go (0154) is theirs alone: no agent can give it
  if (p.intentKey.startsWith(goIntentKey("" as Ids["ItemId"])))
    return `${head}. It is their go, so only they can give it: withdraw it if the work no longer needs it, or put it first in ${who}'s next brief with what it holds up. Don't ask them again in chat.`
  return `${head}. Decide it yourself if your mandate covers it (planning does), withdraw it if it no longer matters, or put it first in ${who}'s next brief with what it holds up. Don't ask them again in chat.`
}

/** Every loose end open at `now`, in key order. */
export const looseEnds = (s: ProjectState, now: number): ReadonlyArray<LooseEnd> => {
  const out: LooseEnd[] = []
  // decision 0158: the lead's own cards that have waited on a person for days (once per card)
  const leadNow = leadOf(s)
  if (s.project?.status === "active" && leadNow?.status === "active")
    for (const a of agedLeadCards(s, now))
      out.push({ key: `aged:${a.proposal.id}`, agentId: leadNow.id, line: agedCardLine(s, a, now) })
  // cards waiting on a person
  for (const p of s.proposals) {
    if (p.state !== "open" || p.approver._tag !== "human") continue
    // decision 0084 (§5): an agent's own pending card doesn't wake it
    if (p.proposedBy._tag === "agent" || p.proposedBy._tag === "worker") continue
    if (now - p.createdAt < LOOSE_END_AFTER_MS || holdsWakes(p) || isStaleEscalation(s, p)) continue
    out.push({
      key: `card:${p.id}`,
      agentId: cardAgent(s, p),
      line: `${p.ref} "${firstLine(p.oneLine)}" waits on ${nameOf(s, recipientOf(s, p))} for ${age(now - p.createdAt)}`,
    })
  }
  // open pull requests (decision 0059's PR view)
  for (const a of s.artifacts) {
    const snap = a.pr?.snapshot
    if (snap?.state !== "open") continue
    const failing = snap.checks.state === "failure" || snap.checks.state === "error"
    const conflicts = snap.mergeable === "conflicting"
    const stale = now - a.createdAt >= PR_LOOSE_AFTER_MS
    const state = failing ? "failing" : conflicts ? "conflicts" : stale ? "stale" : null
    if (state === null) continue
    const t = a.itemId ? itemOf(s, a.itemId) : undefined
    const what =
      state === "failing"
        ? "checks are failing"
        : state === "conflicts"
          ? "has conflicts"
          : `open for ${age(now - a.createdAt)}`
    out.push({
      key: `pr:${a.id}:${state}`,
      agentId: itemAgent(s, t),
      line: `PR #${snap.number}${t ? ` on ${t.ref}` : ""} ${what}`,
    })
  }
  // KPIs of active goals without a number for twice their cadence
  const goalKpis = new Set(
    s.goals
      .filter((g) => g.status === "active")
      .flatMap((g) => [...g.kpiIds, ...(g.target ? [g.target.kpiId] : [])]),
  )
  for (const k of s.kpis) {
    if (!goalKpis.has(k.id)) continue
    const last = k.latest?.at ?? k.createdAt
    if (now - last < 2 * CADENCE_MS[k.cadence]) continue
    // decision 0097: say what feeds it, so the agent fixes the loop rather than typing a number
    const feeders = kpiFeeders(s, k)
    const how = !k.recipe
      ? "; it has no recipe"
      : feeders.length === 0
        ? "; its recipe has no recurring item"
        : `; fed by ${feeders.map((t) => t.ref).join(", ")}`
    out.push({
      key: `kpi:${k.id}`,
      agentId: null,
      line: k.latest
        ? `${k.ref} ${k.name} has no new number for ${age(now - last)} (${k.cadence})${how}`
        : `${k.ref} ${k.name} has no number yet (${k.cadence})${how}`,
    })
  }
  // asks to an agent nobody answered
  for (const c of s.openAsks) {
    if (c.ask?.state !== "open" || now - c.createdAt < LOOSE_END_AFTER_MS) continue
    const who = s.agents.find((x) => x.id === c.ask?.to)
    out.push({
      key: `ask:${c.id}`,
      agentId: c.ask.to,
      line: `@${who?.handle ?? "an agent"} was asked ${c.tainted ? "something" : `"${firstLine(c.body)}"`} ${age(now - c.createdAt)} ago, no answer`,
    })
  }
  // decision 0142: a message to a person's dot or coding session nobody picked up for a day (a closed session): its
  // sender hears of it and decides (an item, a card, or drop it). A visible state, not a limit: it stays deliverable
  for (const t of s.threads ?? [])
    for (const p of t.pending) {
      if (!p.sender || now - p.at < LOOSE_END_AFTER_MS) continue
      const party = t.parties.find((x) => x._tag === "external" && x.participantId === p.participantId)
      const owner = s.members.find((m) => m.userId === p.ownerId)?.handle
      const name =
        party?._tag === "external" ? `@${owner ? `${owner}.` : ""}${party.handle}` : "a participant"
      out.push({
        key: `undelivered:${p.commentId}:${p.participantId}`,
        agentId: p.sender,
        line: `Your message in ${t.ref} to ${name} has not been picked up for ${age(now - p.at)}${p.kind === "session" ? " (the session may be closed)" : ""}: make it an item, a card, or let it go`,
      })
    }
  // items stuck on a blocker (a card or an ask already counts above)
  for (const t of s.items) {
    if (t.status !== "blocked" || !t.blocked) continue
    if (t.blocked._tag === "needs_human" || t.blocked._tag === "question") continue
    if (now - t.updatedAt < LOOSE_END_AFTER_MS) continue
    const what =
      t.blocked._tag === "reported"
        ? t.blocked.blocker._tag === "depends_on"
          ? `waits on ${itemOf(s, t.blocked.blocker.itemId)?.ref ?? "another item"}`
          : firstLine(t.blocked.blocker.what, 80)
        : "claimed impossible"
    out.push({
      key: `stuck:${t.id}`,
      agentId: itemAgent(s, t),
      line: `${t.ref} ${t.title} is blocked for ${age(now - t.updatedAt)}: ${what}`,
    })
  }
  // decision 0154 (review 10-23 #5): items in review with no review running (REP-99 sat 17 h). The key carries the round
  // and stage, so the same wait is noted once
  for (const r of idleReviews(s, now))
    out.push({
      key: `review:${r.item.id}:r${r.item.review.round}:${r.stage}`,
      agentId: r.agentId,
      line: idleReviewLine(r, now),
    })
  // decision 0139 (memory phase 1, 1.5): memories whose revisit date has come, for the agent that keeps them; the key
  // carries the revision, so a confirmed or corrected memory (a new revision, a new date) is a loose end again only
  // when its new date comes
  for (const r of revisitsDue(s, now))
    out.push({ key: `revisit:${r.item.id}:r${r.item.revision}`, agentId: r.agentId, line: r.line })
  // decision 0146 (L5): the lead's memory tending, every few memory changes (its judgement whether a pass is worth it)
  const tendLead = leadOf(s)
  if (s.project?.status === "active" && tendLead?.status === "active") {
    const tend = memoryTendingDue(s, now)
    if (tend) out.push({ key: tend.key, agentId: tendLead.id, line: tend.line })
  }
  out.push(...pagesLooseEnds(s, now))
  return out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)).slice(0, MAX_LOOSE_ENDS)
}

/** Decision 0139: a memory due for a re-check, the agent that keeps it (null: the lead) and its one line. */
export interface RevisitDue {
  readonly item: MemoryItem
  readonly agentId: AgentId | null
  readonly line: string
}

/**
 * Decision 0139 (memory phase 1, 1.5): accepted memories whose revisit date (`learning.revisitAt`) has come, oldest
 * date first. An area's memory is its area lead's to re-check, the project's the lead's (null).
 */
export const revisitsDue = (s: ProjectState, now: number): ReadonlyArray<RevisitDue> => {
  const out: Array<RevisitDue & { readonly at: number }> = []
  for (const m of s.memory) {
    // decision 0146: a superseded learning is history, not a claim to re-check
    if (m.status !== "accepted" || m.content._tag !== "learning" || m.supersededBy !== undefined) continue
    const at = m.content.revisitAt
    if (at > now) continue
    const agentId = m.scope._tag === "area" ? (areaLeadOf(s, m.scope.areaId)?.id ?? null) : null
    const day = new Date(at).toISOString().slice(0, 10)
    out.push({
      item: m,
      agentId,
      at,
      line: `${m.ref ?? m.id} "${firstLine(m.title, 100)}" (learning, ${m.content.confidence} confidence) was due for a re-check on ${day}`,
    })
  }
  return out.sort((a, b) => a.at - b.at || (a.item.id < b.item.id ? -1 : 1))
}

/** Nothing planned re-wakes the lead every this many days (the key's step). */
export const IDLE_STEP_MS = 3 * DAY
/** Decision 0098: a lead's brief is due again this long after its last one (under a day, so a daily check finds it). */
export const BRIEF_AFTER_MS = 20 * HOUR
/** A run waiting this long for a runner is a loose end (decision 0156: a few hours, was 12 h; the check runs daily). */
export const RUNNER_WAIT_MS = 2 * HOUR
const PLANNED: ReadonlySet<string> = new Set(["todo", "in_progress", "in_review", "blocked"])
/** Readiness of a `todo` item that is not work that can start: parked above, or a container whose children decide. */
const NOT_STARTABLE: ReadonlySet<string> = new Set([
  "not_now",
  "empty",
  "has_open_children",
  "ready_to_close",
])

/**
 * Decision 0158 (lead-experience G7): open work that can start or is under way. In progress, in review and blocked
 * work counts; a `todo` item counts only when it is not parked above (an imported release waiting for its Start holds
 * its leaves `not_now`) and is not a container (its children count for it). Series and occurrences aside.
 */
export const plannedWork = (s: ProjectState, now: number): boolean =>
  s.items.some(
    (t) =>
      isOpenItem(t) &&
      PLANNED.has(t.status) &&
      t.recurrence === null &&
      !seriesOf(s, t) &&
      (t.status !== "todo" || !NOT_STARTABLE.has(readiness(s, t.id, now)._tag)),
  )

/**
 * Decision 0158: top-level items in Ready (todo) with open work under them, waiting for a person's Start (an import
 * hands its releases over this way), oldest first.
 */
export const waitingForStart = (s: ProjectState): ReadonlyArray<Item> =>
  s.items
    .filter(
      (t) =>
        t.parentId === null &&
        t.status === "todo" &&
        !t.paused &&
        s.items.some((c) => c.parentId === t.id && isOpenItem(c)),
    )
    .sort((a, b) => a.createdAt - b.createdAt)

/** A series' occurrence (an item under a repeating item). */
const seriesOf = (s: ProjectState, t: Item): Item | undefined => {
  const parent = t.parentId ? itemOf(s, t.parentId) : undefined
  return parent?.recurrence ? parent : undefined
}

/** Decision 0130 (0126 §9): what one person's brief is about. Refs only; the lead reads the records. */
export interface BriefConcern {
  readonly userId: UserId
  readonly handle: string
  /** A project admin: the project headline is theirs even with nothing of their own. */
  readonly admin: boolean
  /** Open cards waiting on them (Needs you, routed per decision 0127). */
  readonly cards: ReadonlyArray<string>
  /** Items they asked for that someone else changed since their last brief. */
  readonly asks: ReadonlyArray<string>
  /** Open items they own that someone else changed since their last brief. */
  readonly owns: ReadonlyArray<string>
  /**
   * Decision 0135: runs that didn't work since their last brief and concern them (turns they asked for, work on their
   * items; failed, or ended without a report). The brief says what didn't work.
   */
  readonly failed: ReadonlyArray<string>
}

/** At most this many refs per kind in a brief-due line (the lead reads the rest). */
const CONCERN_REFS = 5

/**
 * Decision 0130 (0126 §9): the people a lead's briefs are for, and what concerns each: their open cards, their asks and
 * the items they own that changed since the lead last briefed them (by someone else: what they did themselves they
 * know). Project admins always get one (the project headline). Members with nothing get none. Working members only,
 * in roster order.
 */
export const briefConcerns = (s: ProjectState, leadId: AgentId, now: number): ReadonlyArray<BriefConcern> => {
  const out: BriefConcern[] = []
  const lastAny = s.briefs?.[leadId]
  for (const m of s.members) {
    if (!isWorkingMember(s, m.userId)) continue
    const userId = m.userId
    const lastTo =
      s.briefsTo?.[leadId]?.[userId]?.at ?? (lastAny && lastAny.to === userId ? lastAny.at : undefined)
    const since = lastTo ?? Math.max(s.project?.createdAt ?? 0, now - DAY)
    const viewer = { _tag: "user", userId } as const
    const cards = s.proposals.filter((p) => isCardFor(s, viewer, p)).map((p) => p.ref)
    const byOthers = (t: Item) =>
      t.updatedAt > since && !(t.updatedBy._tag === "user" && t.updatedBy.userId === userId)
    // asked for: the item's asker, or the person who created it (an agent's later shaping doesn't make it less theirs)
    const askedBy = (t: Item) =>
      (t.askedBy?._tag === "user" && t.askedBy.userId === userId) ||
      (t.createdBy._tag === "user" && t.createdBy.userId === userId)
    const asks = s.items.filter((t) => askedBy(t) && byOthers(t)).map((t) => t.ref)
    const owns = s.items
      .filter(
        (t) =>
          isOpenItem(t) &&
          t.owner?._tag === "user" &&
          t.owner.userId === userId &&
          byOthers(t) &&
          !asks.includes(t.ref),
      )
      .map((t) => t.ref)
    const admin = isProjectAdmin(s, userId)
    const failed = failedRunsFor(s, userId, since)
    if (!admin && cards.length + asks.length + owns.length + failed.length === 0) continue
    out.push({ userId, handle: m.handle, admin, cards, asks, owns, failed })
  }
  return out
}

const refsText = (label: string, refs: ReadonlyArray<string>): string | null =>
  refs.length === 0
    ? null
    : `${label} ${refs.slice(0, CONCERN_REFS).join(", ")}${refs.length > CONCERN_REFS ? ` +${refs.length - CONCERN_REFS}` : ""}`

const concernLine = (c: BriefConcern): string => {
  const parts = [
    c.admin ? "admin: the headline" : null,
    refsText("cards", c.cards),
    refsText("asked", c.asks),
    refsText("owns", c.owns),
    refsText("didn't work", c.failed),
  ].filter((x): x is string => x !== null)
  return `@${c.handle} (${parts.join("; ")})`
}

/** Decision 0084 (§5): the loose ends of the what's-next loop. */
const pagesLooseEnds = (s: ProjectState, now: number): ReadonlyArray<LooseEnd> => {
  const out: LooseEnd[] = []
  const lead = leadOf(s)
  const leadActive = s.project?.status === "active" && lead?.status === "active"
  const tz = projectZone(s)
  // nothing planned: no Ready, Now, in-review or blocked work (series and occurrences aside), no plan card of the lead's
  // that would make something Ready. The key steps every 3 days, so the lead is woken again while it stays idle
  if (leadActive && lead) {
    const planned = plannedWork(s, now)
    const readyCard = s.proposals.some(
      (p) =>
        p.state === "open" &&
        p.body._tag === "plan_change" &&
        p.proposedBy._tag === "agent" &&
        p.proposedBy.agentId === lead.id &&
        p.body.changes.some((c) => makesReady(s, c) !== null),
    )
    if (!planned && !readyCard) {
      const last = Math.max(
        s.project?.createdAt ?? 0,
        ...s.items.map((t) => t.updatedAt),
        ...s.goals.map((g) => g.updatedAt),
      )
      const idle = Math.max(0, now - last)
      // decision 0158: name the releases that wait for a person's Start (an import's), so the lead can raise them
      const waiting = waitingForStart(s)
      const starts =
        waiting.length === 0
          ? ""
          : ` ${waiting
              .slice(0, 3)
              .map(
                (t) =>
                  `${t.ref} ${JSON.stringify(firstLine(t.title, 60))} waits for its Start (Ready since ${periodOf(t.createdAt, tz)})`,
              )
              .join(
                "; ",
              )}${waiting.length > 3 ? ` and ${waiting.length - 3} more` : ""}: whose Start it needs, and whether to raise it in their brief or propose a first step, is your call.`
      out.push({
        key: `idle:${Math.floor(idle / IDLE_STEP_MS)}`,
        agentId: lead.id,
        line: `Nothing can start or is under way since ${periodOf(last, tz)} (${Math.floor(idle / DAY)} days).${starts} Otherwise work from status.md's Next.`,
      })
    }
  }
  // decision 0098: the lead's briefs are due when its last one is older than BRIEF_AFTER_MS and records changed since
  // (since the last day when there was none) by someone other than a person: what people did themselves they know.
  // The key is the day, so it wakes the lead at most once a day. Decision 0130 (0126 §9): one brief per person, so the
  // line names who has something (briefConcerns); what to write, and whether it is worth their time, is the lead's call
  if (leadActive && lead) {
    const last = s.briefs?.[lead.id]?.at ?? null
    const since = last ?? Math.max(s.project?.createdAt ?? 0, now - DAY)
    if (last === null || now - last >= BRIEF_AFTER_MS) {
      const changed = [...s.items, ...s.goals, ...s.kpis, ...s.proposals].filter(
        (r) => r.updatedAt > since && r.updatedBy._tag !== "user",
      ).length
      const people = briefConcerns(s, lead.id, now)
      // decision 0154 (review 10-23 #6): what was merged over a failing review since the last brief gets its own line
      const over = mergedOverReview(s, since)
      // decision 0158: a card of the lead's that aged since its last brief makes the briefs due once, changes or not
      const aged = agedLeadCards(s, now).filter((a) => last === null || a.agedAt > last)
      // decision 0135: a run that didn't work for someone is news too, even when no record changed
      if ((changed > 0 || aged.length > 0 || people.some((c) => c.failed.length > 0)) && people.length > 0)
        out.push({
          key: `brief:${periodOf(now, tz)}`,
          agentId: lead.id,
          line: `Your briefs are due: ${changed} ${changed === 1 ? "record" : "records"} changed since ${last === null ? "yesterday (no brief yet)" : `your last brief (${periodOf(last, tz)})`}. One \`send_brief\` per person (\`to\`), in sections (headline, their cards as asks, waiting, didntWork, happened, next): ${people.map(concernLine).join("; ")}${people.some((c) => c.failed.length > 0) ? ". Say plainly what didn't work and what happens next" : ""}${over.length > 0 ? `. Merged over a failing review: ${over.map(mergedOverShort).join(", ")}: one didntWork line each for the admins, with who fixes the majors and by when (or the item and date that schedule it)` : ""}${aged.length > 0 ? `. Your cards waiting on a person for days: ${aged.map((a) => agedShort(s, a, now)).join(", ")}: decide or withdraw each, or put it first in that person's brief` : ""}`,
        })
    }
  }
  // work runs (workers, validators) waiting for a runner. A turn waiting for a runner is not one: waking that agent
  // would only queue another turn behind it. Decision 0156 (B4, W-129 waited 14 h unseen): it goes to the lead (who
  // routes work), after a few hours, with Minga's reason in words
  for (const w of longWaits(s, now, RUNNER_WAIT_MS))
    out.push({
      key: `runner:${w.run.id}`,
      agentId: lead?.id ?? itemAgent(s, w.item),
      line: longWaitLine(w, now),
    })
  // occurrences still open when their series came due again
  for (const t of s.items) {
    const series = isOpenItem(t) ? seriesOf(s, t) : undefined
    if (!series?.recurrence) continue
    const next = nextCronAt(series.recurrence.schedule.cron, series.recurrence.schedule.tz, t.createdAt)
    if (next === null || next > now) continue
    const period = /^occ-(\d{4}-\d{2}-\d{2})-/.exec(t.intentKey)?.[1] ?? periodOf(t.createdAt, tz)
    out.push({
      key: `occurrence:${t.id}`,
      agentId: series.owner?._tag === "agent" ? series.owner.agentId : itemAgent(s, series),
      line: `${t.ref} (${firstLine(series.title, 60)}, ${period}) is still open past its next due`,
    })
  }
  // active goals nobody can measure
  for (const g of s.goals) {
    if (g.status !== "active" || g.kpiIds.length > 0 || g.target !== null) continue
    out.push({ key: `goal-kpi:${g.id}`, agentId: null, line: `${g.ref} ${g.title} has no KPI` })
  }
  // the lead's pages the bootstrap leaves to it (§1.3); decision 0086 (§5): an area lead's missing area status, under
  // the same key kind (one `pages:<agentId>` per agent, its paths on one line)
  const missing = new Map<AgentId, string[]>()
  const add = (agentId: AgentId, path: string) =>
    missing.set(agentId, [...(missing.get(agentId) ?? []), path])
  if (leadActive && lead) {
    if (!pageOf(s, "status")) add(lead.id, "project/status.md")
    if (!pageOf(s, "assumptions", lead.id)) add(lead.id, `agents/${lead.handle}/assumptions.md`)
  }
  if (s.project?.status === "active")
    for (const a of areaStatuses(s))
      if (a.owner?.status === "active" && a.page === undefined) add(a.owner.id, areaStatusPath(a.area.slug))
  for (const [agentId, paths] of missing)
    out.push({
      key: `pages:${agentId}`,
      agentId,
      line: `${paths.join(" and ")} ${paths.length === 1 ? "is" : "are"} missing`,
    })
  return out
}

/**
 * Compare with `state.dailyCheck.keys`: `none` (same set: write nothing, wake nobody), `shrink` (nothing new, the set
 * shrank: NoteLooseEnds with no wake), `wake` (NoteLooseEnds; reactions wake each owner of a new key with
 * `loose_ends{keys, lines}`). `keys` is the whole current set.
 */
export type LooseEndsDelta =
  | { readonly _tag: "none" }
  | { readonly _tag: "shrink"; readonly keys: ReadonlyArray<string> }
  | {
      readonly _tag: "wake"
      readonly keys: ReadonlyArray<string>
      readonly wakes: ReadonlyArray<{
        readonly agentId: AgentId
        readonly keys: ReadonlyArray<string>
        readonly lines: ReadonlyArray<string>
      }>
    }

export const looseEndsDelta = (s: ProjectState, now: number): LooseEndsDelta => {
  const current = looseEnds(s, now)
  const keys = current.map((l) => l.key)
  const prev = new Set(s.dailyCheck?.keys ?? [])
  const fresh = current.filter((l) => !prev.has(l.key))
  if (fresh.length === 0) return keys.length === prev.size ? { _tag: "none" } : { _tag: "shrink", keys }
  const lead = s.project?.leadAgentId ?? s.agents.find((a) => a.role === "lead" && a.status !== "retired")?.id
  const per = new Map<AgentId, { keys: string[]; lines: string[] }>()
  for (const l of fresh) {
    const to = l.agentId ?? lead
    if (!to) continue
    const g = per.get(to) ?? { keys: [], lines: [] }
    g.keys.push(l.key)
    g.lines.push(l.line)
    per.set(to, g)
  }
  return { _tag: "wake", keys, wakes: [...per].map(([agentId, g]) => ({ agentId, ...g })) }
}
